import React, { useEffect, useRef, useState } from 'react';
import "./index.scss";
import { Row, Col, Card, Button, Modal, Form } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { useApiServices } from '../../services/api-endpoints/useApiServices';
import { useBreadcrumb } from '../../services/breadcrumb-services/BreadcrumbContext';
import { DateTimeFormat, IsEmptyGuid } from '../../helper/Formatters';
import ImageViewer from '../../components/image-viewer/ImageViewer';
import { toast } from 'react-toastify';
import CommentsBox from '../../components/comments-box';
import ImageUpload from '../../components/image-upload';
import RecentTransactionsBox from '../../components/recent-transactions-box';
import { useLanguage } from '../../services/language/LanguageContext';

// ── Shared UI helpers ────────────────────────────────────────────────────────

const ModernSelect = ({ value, onChange, options, placeholder, icon }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = options.find(o => String(o.id) === String(value));

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(v => !v)} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 9, cursor: 'pointer',
                background: '#fff', border: '1.5px solid #e5e7eb',
                fontSize: 14, color: selected ? '#1f2937' : '#9ca3af',
                textAlign: 'left', outline: 'none', transition: 'border-color 0.15s',
            }}
                onFocus={e => e.currentTarget.style.borderColor = '#6366f1'}
                onBlur={e => e.currentTarget.style.borderColor = '#e5e7eb'}
            >
                {icon && <i className={icon} style={{ fontSize: 12, color: selected ? '#4f46e5' : '#9ca3af', flexShrink: 0 }} />}
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selected ? selected.name : placeholder}
                </span>
                <i className={`fa fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#9ca3af', flexShrink: 0 }} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
                    background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.10)', overflow: 'hidden',
                    maxHeight: 220, overflowY: 'auto',
                }}>
                    {options.map((o) => {
                        const isActive = String(o.id) === String(value);
                        return (
                            <div key={o.id} onClick={() => { onChange(o.id); setOpen(false); }} style={{
                                padding: '9px 14px', cursor: 'pointer', fontSize: 13,
                                display: 'flex', alignItems: 'center', gap: 8,
                                background: isActive ? '#eef2ff' : '#fff',
                                color: isActive ? '#4f46e5' : '#374151',
                                fontWeight: isActive ? 600 : 400,
                                transition: 'background 0.1s',
                            }}
                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#f9fafb'; }}
                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = '#fff'; }}
                            >
                                {isActive && <i className="fa fa-check" style={{ fontSize: 10, color: '#4f46e5', flexShrink: 0 }} />}
                                {!isActive && <span style={{ width: 10, flexShrink: 0 }} />}
                                {o.name}
                            </div>
                        );
                    })}
                    {options.length === 0 && (
                        <div style={{ padding: '12px 14px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>ไม่พบข้อมูล</div>
                    )}
                </div>
            )}
        </div>
    );
};

const SectionLabel = ({ children }) => (
    <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12, marginTop: 4 }}>
        {children}
    </div>
);

const InfoRow = ({ label, value }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
        <div style={{ fontSize: 13, color: '#1f2937', wordBreak: 'break-word' }}>{value || '-'}</div>
    </div>
);

// ── Main component ───────────────────────────────────────────────────────────

const ProductsDetails = () => {
    const { id } = useParams();
    const api = useApiServices();
    const { t } = useLanguage();
    const { setCustomName } = useBreadcrumb();

    const [data, setData] = useState({});
    const [transactions, setTransactions] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showModalGroup, setShowModalGroup] = useState(false);
    const [file, setFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submittingGroup, setSubmittingGroup] = useState(false);
    const [categories, setCategories] = useState([]);
    const [productGroups, setProductGroups] = useState([]);

    const [formData, setFormData] = useState({
        nameTh: '', nameEn: '', descriptionTh: '', descriptionEn: '',
        price: 0, sku: '', sequence: 0, image: null,
        categoryId: null, productGroupId: null, unitTh: '', unitEn: '',
    });
    const [formDataGroup, setFormDataGroup] = useState({ name: '', description: '', shortCode: '' });

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'sequence' ? parseInt(value) : value }));
    };

    const handleChangeGroup = (e) => {
        const { name, value } = e.target;
        setFormDataGroup(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        const payload = { ...formData, image: file?.id ?? null };
        const { isSuccess } = await api.UpdateProductsAsync(id, payload);
        setSubmitting(false);
        if (isSuccess) {
            toast.success(t('save'));
            setShowModal(false);
            fetchProduct(id);
        } else {
            toast.error('เกิดข้อผิดพลาด');
        }
    };

    const handleSubmitGroup = async (e) => {
        e.preventDefault();
        setSubmittingGroup(true);
        const { isSuccess } = await api.AddProductGroupAsync(formDataGroup);
        setSubmittingGroup(false);
        if (isSuccess) {
            toast.success(t('save'));
            setShowModalGroup(false);
            setFormDataGroup({ name: '', description: '', shortCode: '' });
            fetchProductGroups();
        } else {
            toast.error('เกิดข้อผิดพลาด');
        }
    };

    // ── Fetchers ──────────────────────────────────────────────────────────────

    const fetchProduct = async (id) => {
        const { isSuccess, result } = await api.GetProductsDetailsAsync(id);
        if (isSuccess) {
            setData(result);
            setCustomName(result.nameTh);
            const txRes = await api.GetProductsRecentTransactionsAsync(id);
            if (txRes.isSuccess) setTransactions(txRes.result);
        }
    };

    const fetchCategories = async () => {
        const { isSuccess, result } = await api.GetCategoriesAsync();
        if (isSuccess) setCategories(result);
    };

    const fetchProductGroups = async () => {
        const { isSuccess, result } = await api.GetProductGroupsAsync();
        if (isSuccess) setProductGroups(result);
    };

    const fetchFileMeta = async (fileId) => {
        const { isSuccess, result } = await api.GetFileMetaDataAsync(fileId);
        if (isSuccess && result) setFile(result);
    };

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (showModal) {
            setFormData({
                nameTh: data.nameTh || '', nameEn: data.nameEn || '',
                descriptionTh: data.descriptionTh || '', descriptionEn: data.descriptionEn || '',
                price: data.price || 0, sku: data.sku || '', sequence: data.sequence || 0,
                image: data.image || null, categoryId: data.categoryId || null,
                productGroupId: data.productGroupId || null,
                unitTh: data.unitTh || '', unitEn: data.unitEn || '',
            });
            if (data.image) fetchFileMeta(data.image);
            fetchCategories();
            fetchProductGroups();
        }
    }, [showModal]);

    useEffect(() => {
        if (id) fetchProduct(id);
    }, [id]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <Row>
                {/* ── Left: Info panel ──────────────────────────────── */}
                <Col md={4}>
                    {/* Product image + name card */}
                    <Card style={cardStyle}>
                        <Card.Body style={{ padding: '24px 20px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                                <div style={{ width: 90, height: 90, borderRadius: 18, overflow: 'hidden', border: '2px solid #e5e7eb', background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ImageViewer id={data.image} style={{ width: 90, height: 90, objectFit: 'cover' }} />
                                </div>
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#1f2937', marginBottom: 4 }}>{data.nameTh || '-'}</div>
                            {data.nameEn && <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 8 }}>{data.nameEn}</div>}
                            {data.category && (
                                <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, background: '#e0e7ff', color: '#4f46e5', borderRadius: 10, padding: '2px 10px' }}>
                                    {data.category.name}
                                </span>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Info detail card */}
                    <Card style={{ ...cardStyle, marginTop: 16 }}>
                        <Card.Header style={cardHeaderStyle}>
                            <span style={cardTitleStyle}>
                                <i className="fa fa-info-circle" style={{ marginRight: 8, color: '#4f46e5' }} />{t('general_info')}
                            </span>
                            <button onClick={() => setShowModal(true)} style={editBtnStyle}>
                                <i className="fa fa-pencil" style={{ fontSize: 10 }} />{t('edit')}
                            </button>
                        </Card.Header>
                        <Card.Body style={{ padding: '16px 20px' }}>
                            <InfoRow label="SKU" value={data.sku} />
                            <InfoRow label={t('name_th')} value={data.nameTh ? `${data.nameTh}${data.unitTh ? ` [${data.unitTh}]` : ''}` : null} />
                            <InfoRow label={t('name_en')} value={data.nameEn ? `${data.nameEn}${data.unitEn ? ` [${data.unitEn}]` : ''}` : null} />
                            <InfoRow label={t('description_th')} value={data.descriptionTh} />
                            <InfoRow label={t('description_en')} value={data.descriptionEn} />
                            {data.productGroup && (
                                <InfoRow label={t('col_product_group')} value={`${data.productGroup.name}${data.productGroup.shortCode ? ` (${data.productGroup.shortCode})` : ''}`} />
                            )}
                            <InfoRow label={t('default_price')} value={data.price != null ? `฿${Number(data.price).toLocaleString('th-TH')}` : null} />
                            {data.modifyDate && (
                                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6', fontSize: 11, color: '#9ca3af' }}>
                                    <i className="fa fa-clock-o" style={{ marginRight: 5 }} />แก้ไขล่าสุด: {DateTimeFormat(data.modifyDate)}
                                </div>
                            )}
                            {data.createDate && (
                                <div style={{ marginTop: 4, fontSize: 11, color: '#9ca3af' }}>
                                    <i className="fa fa-calendar-o" style={{ marginRight: 5 }} />{t('created_date')}: {DateTimeFormat(data.createDate)}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* ── Right: Transactions + Comments ─────────────────── */}
                <Col md={8}>
                    <Card style={{ ...cardStyle, marginBottom: 16 }}>
                        <Card.Body style={{ padding: 0 }}>
                            <RecentTransactionsBox data={transactions} />
                        </Card.Body>
                    </Card>
                    {!IsEmptyGuid(data.entityUniqueIdentifierId) && (
                        <Card style={cardStyle}>
                            <Card.Body style={{ padding: '20px 24px' }}>
                                <CommentsBox euid={data.entityUniqueIdentifierId} />
                            </Card.Body>
                        </Card>
                    )}
                </Col>
            </Row>

            {/* ── Edit Product Modal ──────────────────────────────────── */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '18px 24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg, #1e3a5f, #2d6a9f)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fa fa-pencil" style={{ fontSize: 15, color: '#fff' }} />
                        </div>
                        <div>
                            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>แก้ไขสินค้า</div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{data.nameTh}</div>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '20px 24px' }}>
                    <Form onSubmit={handleSubmit}>

                        {/* Image */}
                        <SectionLabel>{t('product_image')}</SectionLabel>
                        <div style={{ marginBottom: 20 }}>
                            <ImageUpload forceCrop={true} onUploadComplete={(f) => setFile(f)} />
                        </div>

                        {/* Classification */}
                        <SectionLabel>{t('product_basic_info')}</SectionLabel>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Label style={labelStyle}>{t('col_category')}</Form.Label>
                                <ModernSelect
                                    value={formData.categoryId}
                                    onChange={(id) => setFormData(prev => ({ ...prev, categoryId: id }))}
                                    options={categories}
                                    placeholder={t('select_category')}
                                    icon="fa fa-tag"
                                />
                            </Col>
                            <Col md={5}>
                                <Form.Label style={labelStyle}>{t('col_product_group')}</Form.Label>
                                <ModernSelect
                                    value={formData.productGroupId}
                                    onChange={(id) => setFormData(prev => ({ ...prev, productGroupId: id }))}
                                    options={productGroups}
                                    placeholder={t('select_product_group')}
                                    icon="fa fa-folder-o"
                                />
                            </Col>
                            <Col md={1} style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: 1 }}>
                                <button type="button" title="สร้างกลุ่มใหม่" onClick={() => { setShowModal(false); setShowModalGroup(true); }} style={{
                                    width: 36, height: 36, borderRadius: 9, border: '1.5px solid #c7d2fe',
                                    background: '#f0f4ff', color: '#4f46e5', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                }}>
                                    <i className="fa fa-plus" style={{ fontSize: 12 }} />
                                </button>
                            </Col>
                        </Row>

                        {/* Names */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('name_th')} <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                                    <Form.Control name="nameTh" value={formData.nameTh} onChange={handleChange} required style={inputStyle} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('name_en')}</Form.Label>
                                    <Form.Control name="nameEn" value={formData.nameEn} onChange={handleChange} style={inputStyle} />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Descriptions */}
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('description_th')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="descriptionTh" value={formData.descriptionTh} onChange={handleChange} style={{ ...inputStyle, resize: 'none' }} />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('description_en')}</Form.Label>
                                    <Form.Control as="textarea" rows={3} name="descriptionEn" value={formData.descriptionEn} onChange={handleChange} style={{ ...inputStyle, resize: 'none' }} />
                                </Form.Group>
                            </Col>
                        </Row>

                        {/* Pricing & identifiers */}
                        <SectionLabel>{t('product_pricing_info')}</SectionLabel>
                        <Row className="mb-2">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('default_price')}</Form.Label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#6b7280', fontWeight: 600, pointerEvents: 'none' }}>฿</span>
                                        <Form.Control name="price" type="number" value={formData.price} onChange={handleChange} style={{ ...inputStyle, paddingLeft: 24 }} />
                                    </div>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>{t('col_sku')}</Form.Label>
                                    <Form.Control name="sku" value={formData.sku} onChange={handleChange} style={{ ...inputStyle, fontFamily: 'monospace' }} />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>หน่วย (TH)</Form.Label>
                                    <Form.Control name="unitTh" value={formData.unitTh} onChange={handleChange} style={inputStyle} placeholder="เช่น กล่อง" />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label style={labelStyle}>หน่วย (EN)</Form.Label>
                                    <Form.Control name="unitEn" value={formData.unitEn} onChange={handleChange} style={inputStyle} placeholder="e.g. Box" />
                                </Form.Group>
                            </Col>
                        </Row>

                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '14px 24px', gap: 8 }}>
                    <Button variant="light" onClick={() => setShowModal(false)} style={{ fontSize: 14, borderRadius: 8 }}>{t('cancel')}</Button>
                    <Button onClick={handleSubmit} variant="primary" disabled={submitting} style={{ fontSize: 14, borderRadius: 8, fontWeight: 600, minWidth: 100 }}>
                        {submitting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: 6 }} />{t('saving')}</> : t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* ── Create Product Group Modal ──────────────────────── */}
            <Modal show={showModalGroup} onHide={() => setShowModalGroup(false)} size="sm" centered>
                <Modal.Header closeButton style={{ borderBottom: '1px solid #f3f4f6', padding: '18px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 9, background: 'linear-gradient(135deg, #7c3aed, #4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <i className="fa fa-folder-o" style={{ fontSize: 13, color: '#fff' }} />
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>สร้างกลุ่มสินค้า</div>
                    </div>
                </Modal.Header>
                <Modal.Body style={{ padding: '18px 20px' }}>
                    <Form onSubmit={handleSubmitGroup}>
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>ชื่อกลุ่ม <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                            <Form.Control name="name" value={formDataGroup.name} onChange={handleChangeGroup} required style={inputStyle} placeholder="ระบุชื่อกลุ่ม" />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Short Code <span style={{ color: '#ef4444' }}>*</span></Form.Label>
                            <Form.Control name="shortCode" value={formDataGroup.shortCode} onChange={handleChangeGroup} required style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase' }} placeholder="GRP-01" />
                        </Form.Group>
                        <Form.Group className="mb-1">
                            <Form.Label style={labelStyle}>รายละเอียด</Form.Label>
                            <Form.Control as="textarea" rows={2} name="description" value={formDataGroup.description} onChange={handleChangeGroup} style={{ ...inputStyle, resize: 'none' }} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ borderTop: '1px solid #f3f4f6', padding: '12px 20px', gap: 8 }}>
                    <Button variant="light" onClick={() => setShowModalGroup(false)} style={{ fontSize: 14, borderRadius: 8 }}>{t('cancel')}</Button>
                    <Button onClick={handleSubmitGroup} variant="primary" disabled={submittingGroup} style={{ fontSize: 14, borderRadius: 8, fontWeight: 600 }}>
                        {submittingGroup ? <i className="fa fa-spinner fa-spin" /> : t('save')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const cardStyle = { borderRadius: 12, border: 'none', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' };
const cardHeaderStyle = { background: '#fff', borderBottom: '1px solid #f3f4f6', borderRadius: '12px 12px 0 0', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const cardTitleStyle = { fontWeight: 700, fontSize: 14, color: '#111827' };
const editBtnStyle = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: 8, color: '#4f46e5', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const labelStyle = { fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 };
const inputStyle = { fontSize: 14, borderRadius: 8, border: '1.5px solid #e5e7eb' };

export default ProductsDetails;
